import type { SeatDto } from '@okn/shared';
import { LEVEL_LABELS_TR } from '@okn/shared';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Countdown } from './Countdown.tsx';

type Props = {
  selected: SeatDto[];
  expiresAt: string | null;
  onRemove: (seatId: string) => void;
  onClearAll: () => void;
  onCheckout: () => void;
  errorMessage?: string | null;
};

const spring = { type: 'spring', stiffness: 280, damping: 32 } as const;

// Desktop ≥640px gets the right-edge drawer; below that a compact bottom bar so
// the seat map stays usable on phones (selecting a seat must not cover it).
function useIsDesktop() {
  const [d, setD] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const on = () => setD(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return d;
}

export function CheckoutPanel({
  selected,
  expiresAt,
  onRemove,
  onClearAll,
  onCheckout,
  errorMessage,
}: Props) {
  const isDesktop = useIsDesktop();
  const anim = isDesktop
    ? {
        initial: { x: '100%', opacity: 0.4 },
        animate: { x: 0, opacity: 1 },
        exit: { x: '100%', opacity: 0 },
      }
    : {
        initial: { y: '100%', opacity: 0.6 },
        animate: { y: 0, opacity: 1 },
        exit: { y: '100%', opacity: 0 },
      };

  return (
    <AnimatePresence>
      {selected.length > 0 && (
        <motion.aside
          key="cart"
          initial={anim.initial}
          animate={anim.animate}
          exit={anim.exit}
          transition={spring}
          className="drawer-surface fixed inset-x-0 bottom-0 z-30 flex max-h-[80dvh] flex-col rounded-t-2xl border-t border-border shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.55)] sm:inset-x-auto sm:bottom-0 sm:right-0 sm:top-0 sm:max-h-none sm:h-dvh sm:w-full sm:max-w-[380px] sm:rounded-none sm:border-l sm:border-t-0 sm:shadow-none"
          aria-label="Seçilen koltuklar"
        >
          {/* Header */}
          <header className="border-b border-border px-5 py-4 sm:px-7 sm:pt-7 sm:pb-5">
            <div className="kicker mb-1 sm:mb-2">Seçim</div>
            <div className="flex items-end justify-between gap-4">
              <div className="display text-[30px] font-light leading-none tnum sm:text-[44px]">
                {selected.length}
                <span className="ml-2 text-[12px] uppercase tracking-[0.2em] text-text-faint sm:text-[13px]">
                  koltuk
                </span>
              </div>
              <Countdown expiresAt={expiresAt} large />
            </div>
          </header>

          {/* Seat list — hidden on mobile so the bar stays compact and the map
              stays selectable; selected seats are already highlighted on the map. */}
          <motion.ul
            className="hidden flex-1 overflow-y-auto px-4 py-3 sm:block"
            variants={{
              visible: { transition: { staggerChildren: 0.025 } },
            }}
            initial="hidden"
            animate="visible"
          >
            {selected.map((s) => (
              <motion.li
                key={s.id}
                layout
                variants={{
                  hidden: { opacity: 0, x: 16 },
                  visible: { opacity: 1, x: 0 },
                }}
                transition={spring}
                className="group flex items-center gap-4 rounded-md px-3 py-3 transition-colors hover:bg-surface"
              >
                <div className="display flex h-10 w-10 items-center justify-center rounded-md border border-border-strong bg-surface text-[11px] tracking-wider text-accent tnum">
                  {s.label.split('-')[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] mono">{s.label}</div>
                  <div className="text-[11px] text-text-faint">
                    {LEVEL_LABELS_TR[s.level]} · sıra {s.rowLabel}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(s.id)}
                  className="text-text-faint opacity-0 transition-opacity hover:text-accent group-hover:opacity-100"
                  aria-label={`${s.label} koltuğunu çıkar`}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </motion.li>
            ))}
          </motion.ul>

          {errorMessage && (
            <div
              role="alert"
              className="border-t border-border bg-accent-faint px-5 py-3 sm:px-7 sm:py-3.5"
            >
              <div className="kicker mb-1 text-accent">Hata</div>
              <div className="text-[12px] text-text-muted">{errorMessage}</div>
            </div>
          )}

          {/* Footer */}
          <footer className="space-y-2.5 border-t border-border px-5 py-4 sm:space-y-3 sm:px-7 sm:py-6">
            <button
              type="button"
              onClick={onCheckout}
              className="group relative block w-full overflow-hidden rounded-md bg-accent px-4 py-3.5 text-[14px] font-medium tracking-wide text-white transition-all hover:bg-accent-hover hover:shadow-[0_8px_24px_-8px_var(--accent)]"
            >
              <span className="flex items-center justify-center gap-3">
                Devam et
                <span
                  aria-hidden
                  className="text-[15px] transition-transform group-hover:translate-x-1"
                >
                  →
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={onClearAll}
              className="block w-full rounded-md border border-border px-4 py-2.5 text-[12px] uppercase tracking-[0.18em] text-text-muted transition-colors hover:border-border-strong hover:text-text"
            >
              Seçimi temizle
            </button>
            <p className="hidden pt-1 text-center text-[11px] uppercase tracking-[0.22em] text-text-faint sm:block">
              Ön rezervasyon · son onay <span className="text-text-muted">19 Haziran 19.30</span>
            </p>
          </footer>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
